/**
 * outbox.js — pending-sale queue for the PWA.
 *
 * Every "Complete Sale" tap:
 *   1. Persists the sale to outbox with status='QUEUED'
 *   2. Decrements local stock (cosmetic — desktop is source of truth)
 *   3. Attempts an immediate POST if online
 *   4. On failure, leaves status='QUEUED' for the next flush
 *
 * Flush triggers:
 *   - `online` event
 *   - `visibilitychange` to visible
 *   - 30s interval while pending entries exist
 *
 * Idempotency: the server enforces UNIQUE(license_key, client_op_id), so
 * any number of retries yield exactly one submission. A 200 with
 * deduped:true is treated as success.
 */
import { db, decrementLocalStock } from './db'
import { submitSale, ApiError } from './api'

const FLUSH_INTERVAL_MS = 30_000

let flushTimer = null
let flushing = false

export async function enqueueSale(payload) {
  const row = {
    client_op_id: payload.client_op_id,
    status: 'QUEUED',
    payload,
    created_at: Date.now(),
    last_error: null,
    attempt_count: 0,
  }
  await db.outbox.add(row)

  // Cosmetic local-stock decrement so the next cart's stock badge is honest.
  for (const item of payload.items) {
    decrementLocalStock(item.drug_id, item.quantity).catch(() => {})
  }

  // Fire an immediate flush — usually completes the round-trip before the
  // cashier turns to the next customer.
  flush().catch(() => {})

  return row
}

export async function flush() {
  if (flushing) return { flushed: 0 }
  flushing = true
  let flushed = 0
  try {
    const pending = await db.outbox.where('status').equals('QUEUED').toArray()
    for (const row of pending) {
      try {
        const res = await submitSale(row.payload)
        await db.outbox.update(row.client_op_id, {
          status: 'SYNCED',
          synced_at: Date.now(),
          server_submission_id: res.submissionId,
          deduped: !!res.deduped,
        })
        flushed++
      } catch (err) {
        const e = err instanceof ApiError ? err : new ApiError(String(err?.message || err), 'UNKNOWN', 0)
        const next = { attempt_count: (row.attempt_count || 0) + 1, last_error: e.message }
        // 4xx (other than 401) is the server saying "this will never succeed"
        // — mark REJECTED so the user can investigate rather than spin forever.
        if (e.status >= 400 && e.status < 500 && e.status !== 401 && e.status !== 408 && e.status !== 429) {
          next.status = 'REJECTED'
        }
        await db.outbox.update(row.client_op_id, next)
        // Stop the loop on a single transport error — every other QUEUED row
        // will fail the same way and we'd just rack up attempt counts.
        if (e.status === 0 || e.status >= 500) break
      }
    }
  } finally {
    flushing = false
  }
  return { flushed }
}

export async function pendingCount() {
  return db.outbox.where('status').equals('QUEUED').count()
}

export function startFlushScheduler() {
  if (flushTimer) return
  // Browser will fire 'online' when the radio comes back; we kick a flush too
  // when the document becomes visible (user opens the PWA after radio-silence).
  window.addEventListener('online', () => { flush().catch(() => {}) })
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') flush().catch(() => {})
  })
  flushTimer = setInterval(async () => {
    const n = await pendingCount()
    if (n > 0) flush().catch(() => {})
  }, FLUSH_INTERVAL_MS)
}

export function stopFlushScheduler() {
  if (flushTimer) { clearInterval(flushTimer); flushTimer = null }
}
